// src/App.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Message, streamChat } from './api';
import { API_URL, APP_NAME } from './config';
import { useAuth } from './hooks/useAuth';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfileModal from './components/ProfileModal';
import ChatHistory from './components/ChatHistory';
import './App.css';

interface ChatMessage extends Message {
  id: string;
  streaming?: boolean;
}

const SUGGESTIONS = [
  'Debug my Python TypeError',
  'Explain async/await in JavaScript',
  'Write a REST API in Express.js',
  'Fix my CSS flexbox layout',
  'Convert this function to TypeScript',
  'Write unit tests for my code',
];

function generateId() { return Math.random().toString(36).slice(2); }

export default function App() {
  // ─── Auth ──────────────────────────────────────────────────────────────────
  const { user, loading: authLoading, login, register, logout, updateAvatar, getToken } =
    useAuth();
  const [authPage, setAuthPage] = useState<'login' | 'register'>('login');

  // ─── Chat state ────────────────────────────────────────────────────────────
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeChatId, setActiveChatId] = useState<number | null>(null);
  const activeChatIdRef = useRef<number | null>(null); // FIX: ref to avoid stale closures
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [showProfile, setShowProfile] = useState(false);

  // ─── Sidebar toggle ────────────────────────────────────────────────────────
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // ─── Edit message state ────────────────────────────────────────────────────
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  const bottomRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // FIX: helper that updates both state and ref together
  const setActiveChat = useCallback((id: number | null) => {
    activeChatIdRef.current = id;
    setActiveChatId(id);
  }, []);

  // ─── Load a saved chat ────────────────────────────────────────────────────
  const loadChat = useCallback(async (id: number) => {
    setActiveChat(id);
    setError('');
    const r = await fetch(`${API_URL}/chats.php?id=${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    const data = await r.json();
    if (!r.ok) return;
    const msgs: ChatMessage[] = (data.chat.messages || []).map((m: any) => ({
      id: generateId(), role: m.role, content: m.content
    }));
    setMessages(msgs);
  }, [getToken, setActiveChat]);

  // ─── Save a message to backend ────────────────────────────────────────────
  const saveMessage = useCallback(async (chatId: number, role: string, content: string) => {
    await fetch(`${API_URL}/chats.php?id=${chatId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ role, content })
    });
  }, [getToken]);

  // ─── Send message ─────────────────────────────────────────────────────────
  const sendMessage = useCallback(async (text?: string) => {
    const content = (text || input).trim();
    if (!content || chatLoading) return;
    setInput('');
    setError('');

    const userMsg: ChatMessage = { id: generateId(), role: 'user', content };
    const assistantId = generateId();
    const assistantMsg: ChatMessage = {
      id: assistantId, role: 'assistant', content: '', streaming: true
    };

    setMessages(prev => [...prev, userMsg, assistantMsg]);
    setChatLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    const history: Message[] = [
      ...messages.map(m => ({ role: m.role, content: m.content })),
      { role: 'user', content }
    ];

    let fullResponse = '';
    try {
      for await (const _ of streamChat(history, (token) => {
        fullResponse += token;
        setMessages(prev =>
          prev.map(m => m.id === assistantId ? { ...m, content: m.content + token } : m)
        );
      }, controller.signal)) {}

      setMessages(prev =>
        prev.map(m => m.id === assistantId ? { ...m, streaming: false } : m)
      );

      // ── Persist to DB ──
      if (user) {
        // FIX: read from ref instead of state to avoid stale closure
        let cid = activeChatIdRef.current;
        if (!cid) {
          const title = content.slice(0, 60);
          const r = await fetch(`${API_URL}/chats.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
            body: JSON.stringify({ title, messages: [] })
          });
          const d = await r.json();
          cid = d.chat_id;
          setActiveChat(cid); // FIX: use helper to update both ref and state
          setHistoryRefresh(n => n + 1);
        }
        if (cid) {
          await saveMessage(cid, 'user', content);
          await saveMessage(cid, 'assistant', fullResponse);
        }
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      setError(
        err.message.includes('fetch') || err.message.includes('Failed')
          ? '⚠ Cannot reach AI server. Make sure your Google Colab session is running.'
          : `Error: ${err.message}`
      );
      setMessages(prev => prev.filter(m => m.id !== assistantId));
    } finally {
      setChatLoading(false);
      abortRef.current = null;
    }
  }, [input, chatLoading, messages, user, getToken, saveMessage, setActiveChat]);

  // ─── Edit message handlers ─────────────────────────────────────────────────
  const startEdit = (msg: ChatMessage) => {
    setEditingId(msg.id);
    setEditText(msg.content);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const submitEdit = useCallback(async (msgId: string) => {
    const content = editText.trim();
    if (!content || chatLoading) return;

    // Find index of the message being edited
    const idx = messages.findIndex(m => m.id === msgId);
    if (idx === -1) return;

    // Keep all messages before the edited one
    const before = messages.slice(0, idx);

    setEditingId(null);
    setEditText('');
    setError('');

    const updatedUserMsg: ChatMessage = { id: generateId(), role: 'user', content };
    const assistantId = generateId();
    const assistantMsg: ChatMessage = {
      id: assistantId, role: 'assistant', content: '', streaming: true
    };

    setMessages([...before, updatedUserMsg, assistantMsg]);
    setChatLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    const history: Message[] = [
      ...before.map(m => ({ role: m.role, content: m.content })),
      { role: 'user', content }
    ];

    let fullResponse = '';
    try {
      for await (const _ of streamChat(history, (token) => {
        fullResponse += token;
        setMessages(prev =>
          prev.map(m => m.id === assistantId ? { ...m, content: m.content + token } : m)
        );
      }, controller.signal)) {}

      setMessages(prev =>
        prev.map(m => m.id === assistantId ? { ...m, streaming: false } : m)
      );

      if (user) {
        // FIX: read from ref instead of state to avoid stale closure
        let cid = activeChatIdRef.current;
        if (!cid) {
          const title = content.slice(0, 60);
          const r = await fetch(`${API_URL}/chats.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
            body: JSON.stringify({ title, messages: [] })
          });
          const d = await r.json();
          cid = d.chat_id;
          setActiveChat(cid); // FIX: use helper to update both ref and state
          setHistoryRefresh(n => n + 1);
        }
        if (cid) {
          await saveMessage(cid, 'user', content);
          await saveMessage(cid, 'assistant', fullResponse);
        }
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      setError(`Error: ${err.message}`);
      setMessages(prev => prev.filter(m => m.id !== assistantId));
    } finally {
      setChatLoading(false);
      abortRef.current = null;
    }
  }, [editText, chatLoading, messages, user, getToken, saveMessage, setActiveChat]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const stopGeneration = () => abortRef.current?.abort();

  const startNewChat = () => {
    setMessages([]);
    setError('');
    setActiveChat(null); // FIX: use helper to update both ref and state
  };

  // ─── Loading splash ────────────────────────────────────────────────────────
  if (authLoading) {
    return (
      <div className="auth-page">
        <div className="auth-loading">⚡ Loading…</div>
      </div>
    );
  }

  // ─── Auth pages ────────────────────────────────────────────────────────────
  if (!user) {
    return authPage === 'login'
      ? <LoginPage onLogin={login} onGoRegister={() => setAuthPage('register')} />
      : <RegisterPage onRegister={register} onGoLogin={() => setAuthPage('login')} />;
  }

  // ─── Main app ──────────────────────────────────────────────────────────────
  return (
    <div className="app">
      {showProfile && (
        <ProfileModal
          user={user}
          token={getToken()}
          onAvatarUpdate={updateAvatar}
          onClose={() => setShowProfile(false)}
          onLogout={() => { logout(); setShowProfile(false); }}
        />
      )}

      {/* Sidebar toggle button (always visible) */}
      <button
        className={`sidebar-toggle ${sidebarOpen ? 'open' : 'closed'}`}
        onClick={() => setSidebarOpen(o => !o)}
        title={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
      >
        {sidebarOpen ? '✕' : '☰'}
      </button>

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? '' : 'sidebar-hidden'}`}>
        <div className="sidebar-header">
          <div className="logo">
            {/* New SVG Logo */}
            <svg className="logo-svg" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="36" height="36" rx="10" fill="#a78bfa"/>
              <path d="M10 13h4l2 3-2 3h-4l-2-3 2-3z" fill="#0f0f17"/>
              <path d="M18 9l2 2-2 2-2-2 2-2z" fill="#0f0f17" opacity="0.6"/>
              <rect x="14" y="17" width="12" height="2" rx="1" fill="#0f0f17"/>
              <rect x="14" y="21" width="8" height="2" rx="1" fill="#0f0f17" opacity="0.7"/>
              <circle cx="26" cy="24" r="4" fill="#60a5fa"/>
              <path d="M24.5 24l1 1 2-2" stroke="#0f0f17" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="logo-text">{APP_NAME}</span>
          </div>
        </div>

        <button className="new-chat-btn" onClick={startNewChat}>+ New Chat</button>

        <div className="sidebar-section">
          <p className="sidebar-label">Chat History</p>
          <ChatHistory
            token={getToken()}
            activeChatId={activeChatId}
            onSelectChat={loadChat}
            onNewChat={startNewChat}
            refreshTrigger={historyRefresh}
          />
        </div>

        <div className="sidebar-section suggestions-section">
          <p className="sidebar-label">Suggestions</p>
          {SUGGESTIONS.map(s => (
            <button key={s} className="suggestion-btn" onClick={() => sendMessage(s)}>{s}</button>
          ))}
        </div>

        {/* User profile footer */}
        <div className="sidebar-footer user-footer" onClick={() => setShowProfile(true)}>
          <div className="user-avatar-sm">
            {user.avatar_url
              ? <img src={user.avatar_url} alt="avatar" />
              : <span>{user.name.charAt(0).toUpperCase()}</span>
            }
          </div>
          <div className="user-info-sm">
            <p className="user-name-sm">{user.name}</p>
            <p className="user-email-sm">{user.email}</p>
          </div>
          <span className="edit-icon">✏️</span>
        </div>
      </aside>

      {/* Main chat */}
      <main className="main">
        <div className="chat-container">
          {messages.length === 0 ? (
            <div className="welcome">
              <div className="welcome-icon">⚡</div>
              <h1>Hello, {user.name.split(' ')[0]}!</h1>
              <p>Your free AI assistant for coding and text tasks.</p>
              <div className="welcome-chips">
                {SUGGESTIONS.slice(0, 3).map(s => (
                  <button key={s} className="chip" onClick={() => sendMessage(s)}>{s}</button>
                ))}
              </div>
            </div>
          ) : (
            messages.map(msg => (
              <div key={msg.id} className={`message ${msg.role}`}>
                <div className="message-avatar">
                  {msg.role === 'user'
                    ? (user.avatar_url
                      ? <img src={user.avatar_url} alt="you"
                          style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                      : '🧑')
                    : '⚡'}
                </div>
                <div className="message-content">
                  {editingId === msg.id ? (
                    <div className="edit-area">
                      <textarea
                        className="edit-textarea"
                        value={editText}
                        onChange={e => setEditText(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            submitEdit(msg.id);
                          }
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        autoFocus
                        rows={3}
                      />
                      <div className="edit-actions">
                        <button className="edit-save-btn" onClick={() => submitEdit(msg.id)}>
                          Send ➤
                        </button>
                        <button className="edit-cancel-btn" onClick={cancelEdit}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <ReactMarkdown
                        components={{
                          code({ node, className, children, ...props }: any) {
                            const match = /language-(\w+)/.exec(className || '');
                            const isBlock = !props.inline;
                            return isBlock ? (
                              <SyntaxHighlighter
                                style={oneDark as any}
                                language={match ? match[1] : 'text'}
                                PreTag="div"
                              >
                                {String(children).replace(/\n$/, '')}
                              </SyntaxHighlighter>
                            ) : (
                              <code className={className} {...props}>{children}</code>
                            );
                          }
                        }}
                      >
                        {msg.content}
                      </ReactMarkdown>
                      {msg.streaming && <span className="cursor" />}
                      {/* Edit button — only for user messages, not while streaming */}
                      {msg.role === 'user' && !chatLoading && !msg.streaming && (
                        <button
                          className="msg-edit-btn"
                          onClick={() => startEdit(msg)}
                          title="Edit message"
                        >
                          ✏️
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))
          )}
          {error && <div className="error-banner">{error}</div>}
          <div ref={bottomRef} />
        </div>

        <div className="input-area">
          <div className="input-box">
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask anything about code or text"
              rows={1}
              disabled={chatLoading}
            />
            {chatLoading ? (
              <button className="send-btn stop" onClick={stopGeneration}>⏹ Stop</button>
            ) : (
              <button className="send-btn" onClick={() => sendMessage()}
                disabled={!input.trim()}>➤</button>
            )}
          </div>
          <p className="disclaimer">Free AI — Powered by Professor Raimal Raja.</p>
        </div>
      </main>
    </div>
  );
}